<script setup lang="ts">
import ObjectPicker from '@/onenetwork/components/object_picker/ObjectPicker.vue'
import AccountCardSmall from '@/onenetwork/components/account_card/AccountCardSmall.vue'
import { Icon } from '@iconify/vue'
import { computed, ref, toRaw, watch } from 'vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { CONTRACT_API } from '@/modules/contract/constants/contract.api.uri.const.ts'
import DateTimeFormated from '@/onenetwork/components/datetime/DateTimeFormated.vue'
import ContractProcessFormated from '@/modules/contract/components/parts/ContractProcessFormated.vue'
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue';

interface Props {
  listingId: number
  contractId: number | null
  editable?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['change', 'show:detail'])
const contractId = ref(props.contractId)
const loading = ref<boolean>(false)
const contractDetail = ref<any>(null)
const invalidListing = ref<boolean>(false)

const editable = computed(() => {
  return props.editable
})

const handleFetchContractDetail = async () => {
  if (!contractId.value) return
  try {
    loading.value = true
    const res = await APIClient.get(CONTRACT_API + '/' + contractId.value)
    contractDetail.value = res.data.data
    invalidListing.value = res.data.data.listing_id && props.listingId != res.data.data.listing_id
  } catch (e: any) {
  } finally {
    loading.value = false
  }
}

const handleShowDetail = () => {
  emit('show:detail')
}

const handleRemove = () => {
  contractDetail.value = null
  emit('change', null)
}

const confirmReplace = (event: Event) => {
  event.preventDefault()
  const cf = confirm(
    'Bạn có chắc chắn muốn thay thế liên kết hợp đồng và tin đăng? Vui lòng kiểm tra kỹ thông tin, thao tác này có thể ảnh hưởng đến hoạt động môi giới.',
  )
  if (cf) {
    emit('change', contractDetail.value)
  }
}

const handleSelectContract = (data: any) => {
  const item = toRaw(data[0])
  contractId.value = item?.item?.id || null
}

watch(
  () => contractId.value,
  () => {
    console.log('watch', contractId.value)
    handleFetchContractDetail()
  },
)
watch(
  () => props.contractId,
  () => {
    contractId.value = props.contractId
    handleFetchContractDetail()
  },
  { immediate: true },
)
</script>

<template>
  <InputText type="hidden" name="customer_id" />
  <InputText type="hidden" name="broker_id" />
  <InputText type="hidden" name="contract_id" />
  <div class="flex flex-col w-full pt-3 gap-y-5">
    <div class="w-full md:flex-3">
      <div class="flex w-full items-center mb-3 gap-3" v-if="editable">
        <object-picker
          api-url="/contracts"
          query-field="code"
          key_field="code"
          display_field="title"
          placeholder="Chọn hợp đồng"
          :hide-preview="true"
          @update:selected="handleSelectContract"
        />
      </div>
      <Divider v-if="editable" />
      <template v-if="invalidListing">
        <Message severity="error" class="w-full mb-5">
          <div class="ml-2">
            <strong>Cảnh báo:</strong>
            Hợp đồng này hiện đang liên kết với tin chính chủ / độc quyền khác. Vui lòng chọn / tạo
            hợp đồng khác hoặc nhấn vào nút bên dưới để xác nhận thay thế liên kết.
          </div>
          <div class="flex flex-row justify-center my-3">
            <button type="button" class="btn btn-cs" @click="confirmReplace">
              Xác nhận thay thế
            </button>
          </div>
        </Message>
      </template>
      <template v-else-if="contractDetail?.id && !contractDetail?.listing_id">
        <Message severity="success" class="w-full mb-5">
          <div class="ml-2">
            <strong>Khả dụng:</strong>
            Hợp đồng này chưa liên kết với tin đăng nào. Vui lòng kiểm tra kỹ thông tin trước khi
            liên kết.
          </div>
          <div class="flex flex-row justify-center my-3">
            <button type="button" class="btn btn-cs" @click="confirmReplace">
              Xác nhận liên kết
            </button>
          </div>
        </Message>
      </template>
      <template v-if="loading">
        <div class="flex justify-center items-center">
          <Icon
            icon="svg-spinners:blocks-shuffle-3"
            width="24"
            height="24"
            style="color: #024eb3"
          />
        </div>
      </template>
      <template v-else>
        <div class="contract-list" v-if="contractDetail?.id">
          <div class="contract-list__item">
            <div class="w-full flex flex-col gap-2">
              <div class="flex flex-1">
                <div class="flex-1">Mã hợp đồng:</div>
                <div class="flex-2">
                  <badge severity="secondary" class="font-bold">{{ contractDetail?.code }}</badge>
                </div>
              </div>
              <div class="flex flex-1">
                <div class="flex-1">Tên hợp đồng:</div>
                <div class="flex-2">
                  <span class="font-bold">{{ contractDetail?.title }}</span>
                  <p>{{ contractDetail?.subtitle }}</p>
                </div>
              </div>
              <div class="flex flex-1">
                <div class="flex-1">Ngày ký:</div>
                <div class="flex-2">
                  <date-time-formated :value="contractDetail.signed_at" />
                </div>
              </div>
              <div class="flex flex-1">
                <div class="flex-1">Ngày thanh lý:</div>
                <div class="flex-2">
                  <date-time-formated :value="contractDetail.liquidated_at" />
                </div>
              </div>
              <div class="flex flex-1">
                <div class="flex-1">Trạng thái:</div>
                <div class="flex-2">
                  <contract-process-formated :value="contractDetail.status" />
                </div>
              </div>
              <div class="flex flex-1">
                <div class="flex-1">Tỉ lệ hoa hồng:</div>
                <div class="flex-2">
                  <ul>
                    <li>Tổng: {{ contractDetail.commission_percent * 100 }}%</li>
                    <li>
                      Môi giới bán:
                      {{
                        contractDetail.commission_percent *
                        contractDetail.seller_broker_percent *
                        100
                      }}%
                    </li>
                    <li>
                      Môi giới mua:
                      {{
                        contractDetail.commission_percent *
                        contractDetail.buyer_broker_percent *
                        100
                      }}%
                    </li>
                  </ul>
                </div>
              </div>
              <!--            <Divider/>-->
              <div class="flex flex-col md:flex-row gap-3">
                <div class="flex flex-1 flex-col">
                  <div class="font-bold mb-3">Chính chủ</div>
                  <account-card-small
                    :data="contractDetail.seller"
                    account-type="customer"
                    class="customer"
                  />
                </div>
                <div class="flex flex-1 flex-col">
                  <div class="font-bold mb-3">Môi giới bán</div>
                  <account-card-small :data="{...contractDetail.seller_broker, id: contractDetail.seller_broker.broker_profile.id}" account-type="broker" />
                </div>
              </div>
            </div>
            <div class="detail-btn">
              <AllowPerm perm="contract.detail" action="hide">
                <div
                  class="flex items-center gap-[3px] hover:text-[#0000FF]"
                  @click="handleShowDetail"
                >
                  <Icon icon="bi:info-circle-fill" /> chi tiết
                </div>
              </AllowPerm>
              <div
                class="flex items-center gap-[3px] hover:text-[#FF0000]"
                @click="handleRemove"
                v-if="editable"
              >
                <Icon icon="mingcute:close-circle-line" /> gỡ
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped></style>
